import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import swal from "sweetalert";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomTable from "../../components/CustomTable";
import MainLayout from "../../components/MainLayout";
import axiosInstance from "../../utils/axiosInstance";
import { checkPermission } from "../../utils/checkPermission";
import { formatCustomDate } from "../../utils/formatters";
import RoleModal from "./modal";

export default function Roles() {
  const [refresh, setRefresh] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const columns = [
    { field: "id", headerName: "ID", width: 70, type: "number" },
    { field: "name", headerName: "Name", width: "250" },
    {
      filed: "permissions",
      headerName: "Permissions",
      width: 500,
      renderCell: (value) => {
        const permissions = value.row.permissions.map(
          (permission) => permission.name,
        );
        return permissions.join(", ");
      },
      sortable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: "250",
      valueGetter: (value) => value && new Date(value),
      renderCell: (value) => formatCustomDate(value.row.created_at),
      type: "date",
    },
    {
      field: "",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      type: "actions",
      renderCell: (value) => (
        <Box>
          {checkPermission("role-edit") && (
            <IconButton
              color="info"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenEdit(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
          {checkPermission("role-delete") && (
            <IconButton
              color="error"
              onClick={() => {
                setCurrentRow(value.row);
                setOpenConfirm(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleModalClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setOpenConfirm(false);
    setCurrentRow(null);
  };

  const handleRoleAdd = async (values) => {
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/roles`,
        values,
      );

      if (response.status === 201) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully added role", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to add role. Please try again.", { icon: "error" });
    }
  };

  const handleRoleEdit = async (values, id) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_API_URL}/roles/${id}`,
        values,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully updated role", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to update role. Please try again.", { icon: "error" });
    }
  };

  const handleRoleDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/roles/${currentRow.id}`,
      );

      if (response.status === 200) {
        setRefresh(!refresh);
        handleModalClose();
        swal("Successfully deleted role", { icon: "success" });
      }
    } catch (error) {
      swal("Failed to delete role. Please try again.", { icon: "error" });
    }
  };

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h6">Roles</Typography>

          {checkPermission("role-create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add new
            </Button>
          )}
        </Box>

        <CustomTable
          columns={columns}
          fetchDataUrl="roles-paginated"
          refresh={refresh}
        />
      </MainLayout>

      <RoleModal
        open={openAdd}
        onClose={handleModalClose}
        onSubmit={handleRoleAdd}
      />

      {currentRow && openEdit && (
        <RoleModal
          open={openEdit}
          onClose={handleModalClose}
          onSubmit={handleRoleEdit}
          role={currentRow}
        />
      )}

      {currentRow && (
        <ConfirmationModal
          open={openConfirm}
          onClose={handleModalClose}
          onConfirm={handleRoleDelete}
          title="Are you sure you want to delete this role?"
          message={
            <span>
              <strong>{currentRow.name}</strong> role will be deleted. Please
              note that this action is not reversible.
            </span>
          }
        />
      )}
    </>
  );
}
